/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { MuiCardMedia } from '@/components/blocks/MuiCardMedia';
import { ProductCardChooseOptions } from '@/components/blocks/ProductCard/parts/ChooseOptions';
import { productCardSX } from '@/components/blocks/ProductCard/styles/card';
import { productCardContentSX } from '@/components/blocks/ProductCard/styles/cardContent';
import {
	productCardMediaSX,
	productCardRibbonSX,
} from '@/components/blocks/ProductCard/styles/cardMedia';
import { productCardDetailsSX } from '@/components/blocks/ProductCard/styles/details';
import { productCardNameSX } from '@/components/blocks/ProductCard/styles/name';
import { Swatch } from '@/components/blocks/Swatch';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useProductCard } from '@/data/Content/_ProductCard';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { ProductType, RibbonImage } from '@/data/types/Product';
import { ContentContext, ContentProvider } from '@/data/context/content';
import { ProductCardContextValue } from '@/data/types/ProductCard';
import { Box, Card, CardContent, Stack, SxProps, Typography } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
//import { useWebPSupportCheck } from 'react-use-webp-support-check';
import { Img } from '@/components/blocks/MaterialImage';
import { hideNoChildSX } from '@/styles/common';
import { productCardPriceSX } from './styles/price';
import { combineSX } from '@/utils/combineSX';
import { ProductCardPrice } from './Price';
import NextHead from 'next/head';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { formatPrice } from '@/utils/formatPrice';
import { HCLBreadcrumb } from '@/data/types/Breadcrumb';

export const ProductCard: FC<{
	product: ProductType;
	parentCrumb?: HCLBreadcrumb[];
	clickAction?: () => void;
	showInventory?: boolean;
	styles?: { [key: string]: SxProps };
	imgLoading?: 'eager' | 'lazy';
}> = ({ product, clickAction, styles, parentCrumb, showInventory, imgLoading = 'lazy' }) => {
	const parentCtxValue = useContext(ContentContext) as ReturnType<typeof useCatalogEntryList>;
	const router = useNextRouter(); 
	const { settings } = useSettings();
	const currency = useMemo(
		() => settings?.defaultCurrency,
		[settings?.defaultCurrency]
	);
	const locale = useMemo(() => router.locale ?? router.defaultLocale, [router]);
	
	const eventProps = useMemo(() => ({ product }), [product]);
	const priceDisplayNLS = useLocalization('PriceDisplay');
	const eaaMsgsNLS = useLocalization('EaaMessages');
	const productCardValue = useProductCard(product);
	const { onSwatch, sku } = productCardValue;

	// const supportsWebP = useWebPSupportCheck(); // Commented un used variable, un comment when ever needed
	// const isWebpON = settings.userData['isWebpON'];
	const { onClick } = useProductEvents(eventProps);
	const routeUrl = useMemo(() => product?.seo?.href as string, [product]);

	const ribbonData: RibbonImage = product?.ribbonImages
		? product?.ribbonImages[0]
		: { ribbonName: '', ribbonPath: '' };
	// TODO later for webp
	/* const backupImg = (sku ?? product).thumbnail;
	const onMediaFallback = (event:any) => event.target.src = backupImg;
	if(supportsWebP && isWebpON && ((sku ?? product).thumbnail.indexOf('webp') === -1)){
		(sku ?? product).thumbnail = (sku ?? product).thumbnail.replace('catalog/thumb','catalog/thumb-webp').replace('.jpg','.webp');
	} */

	const contextValue: ProductCardContextValue = useMemo(
		() => ({ ...parentCtxValue, ...productCardValue, product, routeUrl }),
		[parentCtxValue, product, productCardValue, routeUrl]
	);

	const image = useMemo(
		() =>
			`${settings.userData.StaticHostSecure}${(sku ?? product).thumbnail
				?.replaceAll('hclstore', 'wcsstore')
				.replace('wcsstore/wcsstore', 'wcsstore')}`,
		[product, settings.userData.StaticHostSecure, sku]
	);

	// const shouldShowInventory = //Commented un used variable
	// 	product.items || productIsA(product, TYPES.kit) || productIsA(product, TYPES.sku);

	const offerPriceObj = Array.isArray(product.price)
    	? product.price.find((p) => p.usage === 'Offer'): undefined;

	
	const offerPrice = offerPriceObj?.value;
	const offerPriceNum = product.productPrice?.offer??0;
	const listPriceNum = product.productPrice?.list??0;
	const offerPriceEAA = formatPrice(locale, currency, offerPriceNum);
	const listPriceEAA = formatPrice(locale, currency, listPriceNum);
	const helloRetailCard = 0;
	return (  
		<Card
			onClick={onClick(clickAction)}
			sx={productCardSX(({helloRetailCard}))}
			id={product.partNumber}
			data-testid={product.partNumber}
			component={Linkable}
			href={routeUrl}			
			aria-label={`${eaaMsgsNLS.productLinkPrefix.t()}${product.name}${((offerPriceNum < listPriceNum)?
				eaaMsgsNLS.priceSuffixListAndOfferPLP.t( {offerPriceEAA, listPriceEAA} ):eaaMsgsNLS.priceSuffixOfferPLP.t({ offerPriceEAA }))}`} 			
		>
			{imgLoading === 'eager' ? (
				<NextHead>
					<link rel="preload" href={image} />
				</NextHead>
			) : null}
			{/* <CardActions>
				<ProductCardCompareBox product={product} />
			</CardActions> */}
			<Box sx={productCardDetailsSX}>
				<Box>
					{ribbonData?.ribbonPath ? (
						<Box sx={productCardRibbonSX}>
							<Img
								fetchPriority="high"
								loading={imgLoading}
								decoding="async"
								src={ribbonData.ribbonPath}
								alt={ribbonData.ribbonName}
								tabIndex={-1}
							></Img>
						</Box>
					) : null}
					<MuiCardMedia
						sx={combineSX([productCardMediaSX, styles?.cardImageSx ? styles.cardImageSx : {}])}
						component="img"
						image={image}
						alt={product.name}
						fetchPriority="high"
						loading={imgLoading}
						decoding="async"
						height="200"
						width="200"
						/* onError={onMediaFallback} */
					></MuiCardMedia>
				</Box>
				<CardContent sx={productCardContentSX}>
					<Stack spacing={1}>
						<Typography
							tabIndex={-1}
							variant="h3"
							align="center"
							id={`${product.partNumber}-name`}
							data-testid={`${product.partNumber}-name`}							
							sx={combineSX([productCardNameSX, styles?.cardNameSx ?? styles?.cardNameSx])}
						>
							{product.name}
						</Typography>
						<Stack direction="row" justifyContent="center" spacing={1} sx={hideNoChildSX}>
							{product?.colorSwatches?.map((colorSwatch) => (
								<Swatch
									key={colorSwatch.identifier}
									title={colorSwatch.identifier}
									size="medium"
									image={colorSwatch.image1path}
									data-testid={`product-${colorSwatch.identifier.toLowerCase()}-swatch`}
									id={`product-${colorSwatch.identifier.toLowerCase()}-swatch`}
									onClick={(event) => onSwatch(event, colorSwatch)}
								/>
							))}
						</Stack>
						{product?.productPrice?.min ? (
							<ProductCardPrice price={product.productPrice} />
						) : offerPrice ? (
								<Typography variant="body1" align="center" sx={productCardPriceSX}>
									{offerPrice}
								</Typography> ) : (
							<Typography variant="body1" align="center" sx={productCardPriceSX}>
								{priceDisplayNLS.Labels.Pending.t()}
							</Typography>
						)}
					</Stack>
				</CardContent>
			</Box>
			{showInventory ? (
				<ContentProvider value={contextValue}>
					{/* {shouldShowInventory ? (
						<ProductCardAddToCartWithInventory />
					) : (
						<ProductCardChooseOptions />
					)} */}
					<ProductCardChooseOptions />
				</ContentProvider>
			) : null}
		</Card>
	);
};
